import { useEffect, useState } from 'react'
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore'
import { db } from '~/firebase.client'
import { SectionTitle } from '~/components/Sections'
import Lightbox from '~/components/Lightbox'

/** Force http:// URLs to https:// to prevent mixed-content warnings */
function https(url) { return url ? url.replace(/^http:\/\//i, 'https://') : url }

/** Turn URLs in plain text into clickable links */
function Linkify({ children }) {
  if (typeof children !== 'string') return children
  const urlRegex = /((?:https?:\/\/)?(?:www\.)?(?:youtube\.com|youtu\.be|instagram\.com|facebook\.com|fb\.com|whatsapp\.com|wa\.me|twitter\.com|x\.com|linkedin\.com|t\.me)[^\s]*|https?:\/\/[^\s]+)/gi
  const parts = children.split(urlRegex)
  return parts.map((part, i) => {
    if (!urlRegex.test(part)) return part
    const href = part.match(/^https?:\/\//) ? part : `https://${part}`
    return <a key={i} href={href} target="_blank" rel="noopener noreferrer" style={{ color: '#1a56db', wordBreak: 'break-all' }}>{part}</a>
  })
}

function EventCard({ event, variant, onOpenGallery }) {
  return (
    <article className="event-card">
      <div className="event-card-media">
        {event.imageUrl ? (
          <img
            src={https(event.imageUrl)}
            alt={`${event.title} — event visual`}
            width={800}
            height={500}
            loading="lazy"
            decoding="async"
          />
        ) : (
          <div
            style={{
              width: '100%',
              height: '100%',
              background: 'var(--color-surface-alt)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '2.5rem',
            }}
            aria-hidden="true"
          >
            📅
          </div>
        )}
        <span className={`event-badge event-badge--${variant}`}>
          {variant === 'upcoming' ? 'Upcoming' : 'Past'}
        </span>
      </div>
      <div className="event-card-body">
        <h3 className="event-card-title">{event.title}</h3>
        <p className="event-card-meta">
          <span className="event-card-date">{event.dateLabel || event.date}</span>
          {event.location && (
            <>
              <span className="event-card-sep" aria-hidden="true">·</span>
              <span className="event-card-location">{event.location}</span>
            </>
          )}
        </p>
        {event.description && (
          <p className="event-card-desc"><Linkify>{event.description}</Linkify></p>
        )}
        {event.gallery?.length > 0 && (
          <div className="event-gallery">
            {event.gallery.map((img, i) => (
              <button
                key={i}
                className="event-gallery-thumb"
                onClick={() => onOpenGallery(event.gallery, i)}
                aria-label={`View photo ${i + 1}`}
              >
                <img src={https(img.url)} alt={`${event.title} photo ${i + 1}`} loading="lazy" />
              </button>
            ))}
          </div>
        )}
      </div>
    </article>
  )
}

export function EventsSection({ preview = false }) {
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const [lightbox, setLightbox] = useState(null) // { images, index }

  useEffect(() => {
    const q = query(collection(db, 'events'), orderBy('date', 'desc'))
    const unsub = onSnapshot(q, (snap) => {
      setEvents(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
      setLoading(false)
    })
    return unsub
  }, [])

  const today = new Date().toISOString().split('T')[0]

  const upcoming = events
    .filter((e) => (e.endDate || e.startDate || e.date) >= today)
    .sort((a, b) => (a.startDate || a.date).localeCompare(b.startDate || b.date))

  const past = events
    .filter((e) => (e.endDate || e.startDate || e.date) < today)
    .sort((a, b) => (b.startDate || b.date).localeCompare(a.startDate || a.date))

  const displayUpcoming = preview ? upcoming.slice(0, 2) : upcoming
  const displayPast = preview ? [] : past

  // Hide entire section if no events (also hide while loading to prevent flash)
  if (events.length === 0) return null

  return (
    <section id="events" className="section-pad events-section">
      <div className="container">
        <SectionTitle
          eyebrow="Events"
          title="Upcoming and past programs"
          description="Workshops, seminars, exhibitions, and partner meets hosted by S.J.S Technology."
        />

        {(
          <>
            {displayUpcoming.length > 0 && (
              <div className="events-block">
                <h3 className="events-subheading">Upcoming events</h3>
                <div className="event-grid">
                  {displayUpcoming.map((event) => (
                    <EventCard key={event.id} event={event} variant="upcoming" onOpenGallery={(imgs, i) => setLightbox({ images: imgs, index: i })} />
                  ))}
                </div>
              </div>
            )}

            {!preview && displayPast.length > 0 && (
              <div className="events-block events-block--past">
                <h3 className="events-subheading">Past events</h3>
                <div className="event-grid">
                  {displayPast.map((event) => (
                    <EventCard key={event.id} event={event} variant="past" onOpenGallery={(imgs, i) => setLightbox({ images: imgs, index: i })} />
                  ))}
                </div>
              </div>
            )}

            {preview && (
              <div className="section-view-all">
                <a className="btn btn-ghost-light" href="/events">View all events →</a>
              </div>
            )}
          </>
        )}
      </div>

      {lightbox && (
        <Lightbox
          images={lightbox.images}
          startIndex={lightbox.index}
          onClose={() => setLightbox(null)}
        />
      )}
    </section>
  )
}
